/**
 * TanStack Query hooks for the admin seller-applications queue (Phase 5).
 *
 * `useSellerApplications` — `useInfiniteQuery` over the cursor-paginated list,
 *   30-second refetch interval, default `['submitted','in_review']` status filter.
 * `useQueueCounts`        — `useQuery` for the counts badge, 30-second refetch.
 * `useApplicationDetail`  — `useQuery` for the drawer detail view.
 * `useApproveApplication` / `useRejectApplication` / `useRequestReupload` —
 *   `useMutation` wrappers that invalidate the list + counts caches on success.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  adminQueryKeys,
  approveApplication,
  getApplicationDetail,
  getQueueCounts,
  listSellerApplications,
  rejectApplication,
  requestReupload,
  type ActionAckResponse,
  type ApproveResponse,
  type QueueCountsResponse,
  type SellerApplicationDetail,
  type SellerApplicationListItem,
  type SellerApplicationListResponse,
} from "@/features/admin/api";

// 30-second refetch interval (spec requirement).
const REFETCH_INTERVAL = 30_000;

// ─────────────────────────── Filter state types ────────────────────────────

export interface ApplicationFilters {
  /** Default: ['submitted', 'in_review']. */
  statuses: string[];
  /** Null means all sources. */
  source: string | null;
  /** Client-derived filter: show only status='in_review' AND proof_photo_s3_key==null. */
  awaitingReupload: boolean;
}

export const DEFAULT_FILTERS: ApplicationFilters = {
  statuses: ["submitted", "in_review"],
  source: null,
  awaitingReupload: false,
};

// ─────────────────────────── Infinite list ─────────────────────────────────

export function useSellerApplications(
  filters: ApplicationFilters = DEFAULT_FILTERS,
): UseInfiniteQueryResult<InfiniteData<SellerApplicationListResponse>, Error> {
  return useInfiniteQuery<
    SellerApplicationListResponse,
    Error,
    InfiniteData<SellerApplicationListResponse>,
    ReturnType<typeof adminQueryKeys.applications>,
    string | null
  >({
    queryKey: adminQueryKeys.applications({
      statuses: filters.statuses,
      source: filters.source,
    }),
    queryFn: ({ pageParam }) =>
      listSellerApplications({
        statuses: filters.statuses,
        source: filters.source,
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
}

/**
 * Derive the "awaiting re-upload" discriminator from a list item:
 * `status === 'in_review' && proof_photo_s3_key === null`.
 */
export function isAwaitingReupload(item: SellerApplicationListItem): boolean {
  return item.status === "in_review" && item.proof_photo_s3_key === null;
}

// ─────────────────────────── Counts ────────────────────────────────────────

export function useQueueCounts(): UseQueryResult<QueueCountsResponse, Error> {
  return useQuery<QueueCountsResponse, Error>({
    queryKey: adminQueryKeys.counts(),
    queryFn: getQueueCounts,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
}

// ─────────────────────────── Detail ────────────────────────────────────────

export function useApplicationDetail(
  id: string | null,
): UseQueryResult<SellerApplicationDetail, Error> {
  return useQuery<SellerApplicationDetail, Error>({
    queryKey: adminQueryKeys.detail(id ?? ""),
    queryFn: () => getApplicationDetail(id!),
    enabled: id !== null,
    staleTime: 0, // always re-fetch when the drawer focuses (TTL on presigned URLs).
  });
}

// ─────────────────────────── Mutations ─────────────────────────────────────

/** Invalidates list + counts and the specific detail entry after success. */
function useInvalidateAfterAction(): (id: string) => Promise<void> {
  const qc = useQueryClient();
  return async (id: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "seller-applications"] }),
      qc.invalidateQueries({ queryKey: adminQueryKeys.detail(id) }),
    ]);
  };
}

export function useApproveApplication(): UseMutationResult<
  ApproveResponse,
  Error,
  string
> {
  const invalidate = useInvalidateAfterAction();
  return useMutation<ApproveResponse, Error, string>({
    mutationFn: (id) => approveApplication(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

export function useRejectApplication(): UseMutationResult<
  ActionAckResponse,
  Error,
  { id: string; reason: string }
> {
  const invalidate = useInvalidateAfterAction();
  return useMutation<ActionAckResponse, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => rejectApplication(id, reason),
    onSuccess: (_data, { id }) => invalidate(id),
  });
}

export function useRequestReupload(): UseMutationResult<
  ActionAckResponse,
  Error,
  { id: string; reason: string }
> {
  const invalidate = useInvalidateAfterAction();
  return useMutation<ActionAckResponse, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => requestReupload(id, reason),
    onSuccess: (_data, { id }) => invalidate(id),
  });
}
